import React, { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { Client } from "@microsoft/microsoft-graph-client";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Stack,
  Container,
  Alert,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  AlertTitle,
  Collapse
} from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";

// Icons
import BackupIcon from "@mui/icons-material/Backup";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import StorageIcon from "@mui/icons-material/Storage";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import SecurityIcon from "@mui/icons-material/Security";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

interface OverallAppBackupProps {
  onBack?: () => void;
}

const OverallAppBackup: React.FC<OverallAppBackupProps> = ({ onBack }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(false);
  const [showAboutBackup, setShowAboutBackup] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimatedCount, setEstimatedCount] = useState<number | null>(null);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  const [backupStatus, setBackupStatus] = useState<{
    success?: boolean;
    message?: string;
    appCount?: number;
    timestamp?: string;
  } | null>(null);

  const toggleAboutBackup = () => {
    setShowAboutBackup(!showAboutBackup);
  };

  // Helper to fetch all applications with paging
  const fetchAllApplications = async (client: Client) => {
    let allApps: any[] = [];
    // Start with a larger page size to reduce roundtrips
    let request = client.api("/applications").top(999);

    while (true) {
      const res: any = await request.get();
      if (res?.value?.length) {
        allApps.push(...res.value);
      }
      const nextLink: string | undefined = res["@odata.nextLink"]; 
      if (!nextLink) break;

      // Recreate request from nextLink (strip host if present)
      const relative = nextLink.replace("https://graph.microsoft.com/v1.0", "").replace("https://graph.microsoft.com/beta", "");
      request = client.api(relative);
    }

    return allApps;
  };

  const handleBackupAndDownload = async () => {
    // Your existing backup function code remains unchanged
    setLoading(true);
    setBackupStatus(null);

    try {
      // Attempt silent authentication
      let response = await instance.acquireTokenSilent({
        scopes: ["Application.ReadWrite.All"],
        account: accounts[0],
      });

      // Initialize Graph API client
      const client = Client.init({
        authProvider: (done) => done(null, response.accessToken),
      });

      // Fetch all applications (paged)
      const appsData = await fetchAllApplications(client);

      if (!appsData || appsData.length === 0) {
        setBackupStatus({
          success: false,
          message: "No applications found in the tenant.",
        });
        setLoading(false);
        return;
      }

      // Convert to JSON and trigger download
      const timestamp = new Date().toISOString();
      const backupData = {
        timestamp,
        applications: appsData,
      };

      const jsonData = JSON.stringify(backupData, null, 2);
      const blob = new Blob([jsonData], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      const fileDate = new Date().toISOString().replace(/[:.]/g, "-");
      const fileName = `Tenant_App_Backup_${fileDate}.json`;
      link.download = fileName;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setBackupStatus({
        success: true,
        message: `Backup completed successfully. File: ${fileName}`,
        appCount: appsData.length,
        timestamp: new Date().toLocaleString(),
      });
    } catch (error: any) {
      // Your existing error handling code
      console.error("Backup failed:", error);

      if (error instanceof InteractionRequiredAuthError) {
        try {
          // Prompt for interactive consent
          // Rest of your code for interactive consent handling
          let interactiveResponse = await instance.acquireTokenPopup({
            scopes: ["Application.ReadWrite.All"],
          });

          const client = Client.init({
            authProvider: (done) => done(null, interactiveResponse.accessToken),
          });

          const appsData = await fetchAllApplications(client);

          if (!appsData || appsData.length === 0) {
            setBackupStatus({
              success: false,
              message: "No applications found in the tenant.",
            });
            setLoading(false);
            return;
          }

          // Convert to JSON and trigger download
          const timestamp = new Date().toISOString();
          const backupData = {
            timestamp,
            applications: appsData,
          };

          const jsonData = JSON.stringify(backupData, null, 2);
          const blob = new Blob([jsonData], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          
          const fileDate = new Date().toISOString().replace(/[:.]/g, "-");
          const fileName = `Tenant_App_Backup_${fileDate}.json`;
          link.download = fileName;
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          setBackupStatus({
            success: true,
            message: `Backup completed successfully. File: ${fileName}`,
            appCount: appsData.length,
            timestamp: new Date().toLocaleString(),
          });
        } catch (interactiveError) {
          console.error("Interactive auth failed:", interactiveError);
          setBackupStatus({
            success: false,
            message: "Backup failed: User consent is required. Please check permissions and try again.",
          });
        }
      } else {
        setBackupStatus({
          success: false,
          message: "Backup failed. Please check the console for details.",
        });
      }
    }

    setLoading(false);
  };

  // Try to estimate number of applications using $count
  const fetchApplicationsCount = async () => {
    setEstimateLoading(true);
    setEstimatedCount(null);
    setEstimateError(null);
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ["Application.ReadWrite.All"],
        account: accounts[0],
      });
      const client = Client.init({
        authProvider: (done) => done(null, response.accessToken),
      });
      const res: any = await client
        .api("/applications")
        .header("ConsistencyLevel", "eventual")
        .query({ "$count": "true", "$top": "1" })
        .get();
      const count = res["@odata.count"] as number | undefined;
      if (typeof count === "number") {
        setEstimatedCount(count);
      } else {
        setEstimateError("Count not available");
      }
    } catch (e) {
      console.warn("Unable to estimate application count", e);
      setEstimateError("Unable to estimate application count");
    } finally {
      setEstimateLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Confirm dialog before starting extensive backup */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Back up all applications?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will back up <strong>all applications</strong> in your tenant. In large environments (hundreds or
            thousands of apps), this may take several minutes and produce a large JSON file.
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            {estimateLoading && (
              <>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Fetching estimate…</Typography>
              </>
            )}
            {!estimateLoading && estimatedCount !== null && (
              <Typography variant="body2" color="text.secondary">
                Estimated applications: <strong>{estimatedCount.toLocaleString()}</strong>
              </Typography>
            )}
            {!estimateLoading && estimateError && (
              <Typography variant="body2" color="text.secondary">
                Couldn’t determine total; you can still proceed.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={loading}>Cancel</Button>
          <Button onClick={() => { setConfirmOpen(false); handleBackupAndDownload(); }} variant="contained" color="primary" disabled={loading}>
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
      {/* Header Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f4f8fb',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          {onBack && (
            <Button 
              variant="outlined" 
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              sx={{
                mr: 2,
                borderRadius: 2,
                pl: 1.5,
                color: "text.secondary",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                  borderColor: "primary.main",
                  color: "primary.main"
                }
              }}
            >
              Back
            </Button>
          )}
          
          <Box
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 0.5,
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.01em',
              }}
            >
              Tenant Application Backup
            </Typography>
            
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              }}
            >
              Export a complete backup of all registered applications in your tenant
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Main Content - Now Full Width */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Typography
            variant="h6"
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid rgba(0,0,0,0.12)',
              backgroundColor: '#f9f9f9',
              fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <StorageIcon sx={{ mr: 1, color: 'primary.main' }} />
            Backup Configuration
          </Typography>
          
          <Box sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ mb: 3 }}>
              This tool creates a comprehensive backup of all registered applications in your Azure AD tenant. 
              The backup includes:
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: 'rgba(227, 242, 253, 0.5)', 
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'flex-start'
                }}>
                  <CheckCircleOutlineIcon color="primary" sx={{ mr: 1.5, mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Application Identities
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      App IDs, display names, and platform configurations
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: 'rgba(232, 245, 233, 0.5)', 
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'flex-start'
                }}>
                  <CheckCircleOutlineIcon color="success" sx={{ mr: 1.5, mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Authentication & Permissions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Redirect URIs, token configurations, API permissions
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: 'rgba(255, 243, 224, 0.5)', 
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'flex-start'
                }}>
                  <CheckCircleOutlineIcon sx={{ mr: 1.5, mt: 0.5, color: 'warning.dark' }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      App Roles & APIs
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Defined app roles, exposed APIs, and scopes
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 4 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => { setConfirmOpen(true); fetchApplicationsCount(); }}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudDownloadIcon />}
                sx={{ 
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
                  fontSize: '1rem',
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)'
                  }
                }}
              >
                {loading ? "Processing..." : "Backup & Download"}
              </Button>
              
              {loading && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Gathering application data from your tenant...
                </Typography>
              )}
            </Box>
            
            {backupStatus && (
              <Box sx={{ mt: 4 }}>
                <Alert 
                  severity={backupStatus.success ? "success" : "error"}
                  sx={{ borderRadius: 2 }}
                  action={
                    backupStatus.success && (
                      <Tooltip title="The file has been downloaded to your downloads folder">
                        <IconButton color="inherit" size="small">
                          <HelpOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )
                  }
                >
                  <AlertTitle>
                    {backupStatus.success ? "Backup Successful" : "Backup Failed"}
                  </AlertTitle>
                  {backupStatus.message}
                  
                  {backupStatus.success && backupStatus.appCount && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>{backupStatus.appCount}</strong> applications backed up at {backupStatus.timestamp}
                    </Typography>
                  )}
                </Alert>
              </Box>
            )}
            
            {/* Info Button to toggle About Backup Files section */}
            <Box sx={{ 
              mt: 4, 
              display: 'flex', 
              justifyContent: 'center',
              borderTop: '1px solid rgba(0,0,0,0.08)',
              pt: 3
            }}>
              <Button
                variant="text"
                color="primary"
                onClick={toggleAboutBackup}
                startIcon={showAboutBackup ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                endIcon={<InfoOutlinedIcon />}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 2
                }}
              >
                {showAboutBackup ? "Hide Information" : "About Backup Files"}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      
      {/* About Backup Files - Expandable Section */}
      <Collapse in={showAboutBackup} timeout="auto">
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3, borderColor: 'rgba(0, 0, 0, 0.08)' }}>
          <CardContent sx={{ p: 0 }}>
            <Typography
              variant="h6"
              sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid rgba(0,0,0,0.12)',
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <FindInPageIcon sx={{ mr: 1, color: 'primary.main' }} />
              About Backup Files
            </Typography>
            
            <Box sx={{ p: 3 }}>
              <Typography variant="body1" sx={{ mb: 3 }}>
                The backup is exported as a JSON file containing all application configurations in your tenant. 
                This file can be used for:
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: 'rgba(227, 242, 253, 0.5)', 
                    borderRadius: 2,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-start'
                  }}>
                    <SaveAltIcon color="primary" sx={{ mr: 1.5, mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Documentation & Inventory
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Complete record of all registered apps and their configurations
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: 'rgba(232, 245, 233, 0.5)', 
                    borderRadius: 2,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-start'
                  }}>
                    <SecurityIcon color="success" sx={{ mr: 1.5, mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Security Audits
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Review app permissions, redirect URIs, and other security settings
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: 'rgba(255, 243, 224, 0.5)', 
                    borderRadius: 2,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-start'
                  }}>
                    <StorageIcon sx={{ mr: 1.5, mt: 0.5, color: 'warning.dark' }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Disaster Recovery
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Reference for recreating apps if they're accidentally deleted
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              
              <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 3, borderRadius: 2 }}>
                <AlertTitle>Important Note</AlertTitle>
                The backup does <strong>not</strong> include client secrets or certificates, as these are not retrievable via the Microsoft Graph API.
              </Alert>
            </Box>
          </CardContent>
        </Card>
      </Collapse>
    </Container>
  );
};

export default OverallAppBackup;